import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import CMSPageEditor from "../components/CMSPageEditor";
import {
  Lock, Users, Download, UserPlus, Crown, GraduationCap, 
  Search, Loader2, LogOut, Shield, Mail, Send, Calendar, Clock,
  BarChart3, CheckCircle, XCircle, Settings, Package, Plus, Trash2, Edit, Save,
  Tag, Percent, ToggleLeft, ToggleRight, Filter, FileText, Trophy, Leaf, Target,
  MessageCircle, Eye, EyeOff, Reply, ExternalLink, DollarSign, Gift, X
} from "lucide-react";
import { Badge } from "../components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [password, setPassword] = useState("");
  const [adminToken, setAdminToken] = useState(sessionStorage.getItem("adminToken") || "");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [grantEmail, setGrantEmail] = useState("");
  const [grantTier, setGrantTier] = useState("premium");
  const [grantReason, setGrantReason] = useState("");
  
  // Tab state
  const [activeTab, setActiveTab] = useState("users");
  
  // Newsletter state
  const [newsletters, setNewsletters] = useState([]);
  const [newsletterStats, setNewsletterStats] = useState(null);
  const [newNewsletter, setNewNewsletter] = useState({
    subject: "",
    content: "",
    target_audience: "all",
    target_zone: "",
    target_filters: {},
    schedule_time: ""
  });

  // Site Settings state
  const [siteSettings, setSiteSettings] = useState(null);
  
  // Coupons state
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({
    code: "",
    discount_type: "percent",
    discount_value: 10,
    valid_for_tiers: ["standard", "premium", "lifetime"],
    max_uses: null,
    expires_at: "",
    description: ""
  });

  // Marketplace state
  const [listings, setListings] = useState([]);
  const [editingListing, setEditingListing] = useState(null);
  const [newListing, setNewListing] = useState({
    plant_name: "",
    variety: "",
    description: "",
    price: "",
    quantity_available: "",
    seed_count_per_packet: "",
    year_harvested: new Date().getFullYear(),
    origin_location: "",
    growing_instructions: "",
    planting_zone: "",
    days_to_germination: "",
    is_organic: false,
    is_heirloom: false
  });

  // Lesson Plans state
  const [lessonPlans, setLessonPlans] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [adminEmail, setAdminEmail] = useState("");

  // Challenges state
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [showChallengeForm, setShowChallengeForm] = useState(false);
  const [newChallenge, setNewChallenge] = useState({
    title: "",
    description: "",
    theme: "edible",
    local_theme: "US Virgin Islands",
    prompt: "",
    requirements: [""],
    learning_objectives: [""],
    featured_plants: [""],
    grade_level: "K-12",
    min_plants: 5,
    max_plants: 30,
    yield_goal_lbs: null,
    water_limit_gallons: null,
    start_date: "",
    end_date: "",
    scheduled_publish_date: "",
    status: "scheduled",
    is_public: true,
    is_featured: true
  });

  // Inquiries state
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [inquiryFilter, setInquiryFilter] = useState("all"); // all, unread, general, advertising, press, etc.

  // Teachers state
  const [teachers, setTeachers] = useState([]);
  const [teacherFilter, setTeacherFilter] = useState("all"); // all, pending, verified
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  // Seed Orders state
  const [seedOrders, setSeedOrders] = useState([]);
  const [orderStats, setOrderStats] = useState(null);
  const [orderFilter, setOrderFilter] = useState("all"); // all, pending, confirmed, processing, shipped
  const [selectedOrder, setSelectedOrder] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      const token = response.data.token;
      sessionStorage.setItem("adminToken", token);
      setAdminToken(token);
      toast.success("Admin authenticated");
      loadAllData(token);
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminToken");
    setAdminToken("");
    setUsers([]);
    setNewsletters([]);
  };

  const loadAllData = async (token = adminToken) => {
    await Promise.all([
      loadUsers(token),
      loadNewsletters(token),
      loadSiteSettings(token),
      loadListings(token),
      loadCoupons(token),
      loadLessonPlans(token),
      loadChallenges(token),
      loadInquiries(token),
      loadTeachers(token),
      loadSeedOrders(token)
    ]);
  };

  const loadUsers = async (token = adminToken) => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired");
      }
    }
  };

  const loadNewsletters = async (token = adminToken) => {
    try {
      const [nlResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/admin/newsletters`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/newsletter-stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setNewsletters(nlResponse.data);
      setNewsletterStats(statsResponse.data);
    } catch (error) {
      console.error("Failed to load newsletters:", error);
    }
  };

  const loadSiteSettings = async (token = adminToken) => {
    try {
      const response = await axios.get(`${API}/admin/site-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSiteSettings(response.data);
    } catch (error) {
      console.error("Failed to load site settings:", error);
    }
  };

  const loadListings = async (token = adminToken) => {
    try {
      const response = await axios.get(`${API}/admin/marketplace/listings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(response.data);
    } catch (error) {
      console.error("Failed to load listings:", error);
    }
  };

  const loadCoupons = async (token = adminToken) => {
    try {
      const response = await axios.get(`${API}/admin/coupons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(response.data);
    } catch (error) {
      console.error("Failed to load coupons:", error);
    }
  };

  const loadLessonPlans = async (token = adminToken) => {
    try {
      const response = await axios.get(`${API}/admin/lesson-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLessonPlans(response.data.lessons || []);
    } catch (error) {
      console.error("Failed to load lesson plans:", error);
    }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm("Delete this lesson plan?")) return;
    try {
      await axios.delete(`${API}/admin/lesson-plans/${lessonId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Lesson plan deleted");
      loadLessonPlans();
      setSelectedLesson(null);
    } catch (error) {
      toast.error("Failed to delete lesson plan");
    }
  };

  const openLessonPDF = (lessonId) => {
    window.open(`${API}/admin/lesson-plans/${lessonId}/pdf?token=${adminToken}`, "_blank");
  };

  const updateAdminEmail = async () => {
    if (!adminEmail) {
      toast.error("Email is required");
      return;
    }
    try {
      await axios.post(`${API}/admin/settings/email`, 
        { admin_email: adminEmail },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("Admin email updated! You'll receive lesson plan notifications.");
    } catch (error) {
      toast.error("Failed to update email");
    }
  };

  const downloadAllLessonsPDF = () => {
    if (lessonPlans.length === 0) {
      toast.error("No lesson plans to export");
      return;
    }

    // Generate HTML for all lesson plans bundled together
    const printWindow = window.open("", "_blank");
    
    const lessonsHTML = lessonPlans.map((record, index) => {
      const lesson = record.lesson_plan || {};
      return `
        <div class="lesson-plan ${index > 0 ? 'page-break' : ''}">
          <h1>SAE 5E Lesson Plan | Grow Plan</h1>
          <h2 style="color: #2d5016; margin-bottom: 20px;">${lesson.title || record.title || 'Untitled'}</h2>
          
          <div class="header-info">
            <div><strong>Grade:</strong> ${lesson.grade_level || record.grade_level || 'ALL GRADE LEVELS'}</div>
            <div><strong>Standard:</strong> ${lesson.standard || record.standard || 'N/A'}</div>
            <div><strong>Content Area:</strong> ${lesson.content_area || record.content_area || 'N/A'}</div>
            <div><strong>Date:</strong> ${record.created_at ? record.created_at.substring(0, 10) : 'N/A'}</div>
          </div>
          
          ${lesson.sae_connection ? `<h3>SAE Connection</h3><p>${lesson.sae_connection}</p>` : ''}
          
          ${lesson.visae_standards?.length ? `
            <h3>VISAE Foundation Standards</h3>
            <ul>${lesson.visae_standards.map(s => `<li>${s}</li>`).join('')}</ul>
          ` : ''}
          
          ${lesson.key_concepts ? `<h3>Key Concepts (Fruit)</h3><p>${lesson.key_concepts}</p>` : ''}
          
          ${lesson.sustainable_objective ? `<h3>Sustainable Objective</h3><p><strong>Students will be able to:</strong> ${lesson.sustainable_objective}</p>` : ''}
          
          ${lesson.vocabulary?.length ? `
            <h3>Key Vocabulary (Seasonings)</h3>
            <div class="vocabulary">${lesson.vocabulary.map(v => `<span class="vocab-word">${v}</span>`).join('')}</div>
          ` : ''}
          
          ${lesson.content_objective ? `<h3>Content Objective</h3><p><strong>Students will be able to:</strong> ${lesson.content_objective}</p>` : ''}
          
          <h2 style="margin-top: 30px; border-top: 2px solid #2d5016; padding-top: 20px;">5E Instructional Phases</h2>
          
          ${lesson.engage ? `<div class="phase engage"><h4>ENGAGE</h4><p>${lesson.engage}</p></div>` : ''}
          ${lesson.explore ? `<div class="phase explore"><h4>EXPLORE</h4><p>${lesson.explore}</p></div>` : ''}
          ${lesson.explain ? `<div class="phase explain"><h4>EXPLAIN</h4><p>${lesson.explain}</p></div>` : ''}
          ${lesson.elaborate ? `<div class="phase elaborate"><h4>ELABORATE</h4><p>${lesson.elaborate}</p></div>` : ''}
          ${lesson.evaluate ? `<div class="phase evaluate"><h4>EVALUATE</h4><p>${lesson.evaluate}</p></div>` : ''}
          
          ${lesson.materials?.length ? `
            <h3>Materials & Tools</h3>
            <div class="materials-list">${lesson.materials.map(m => `<div>☐ ${m}</div>`).join('')}</div>
          ` : ''}
          
          ${lesson.sae_lab ? `<h3>In-Class SAE Lab</h3><p>${lesson.sae_lab}</p>` : ''}
          ${lesson.home_learning ? `<h3>Home Learning</h3><p>${lesson.home_learning}</p>` : ''}
          ${lesson.assessment ? `<h3>Assessment (Grow)</h3><p>${lesson.assessment}</p>` : ''}
          
          <!-- Student Worksheets -->
          ${lesson.check_your_roots || lesson.creative_worksheet || lesson.test_your_growth ? `
            <div class="worksheet-section" style="page-break-before: always; margin-top: 40px;">
              <h2 style="text-align: center; border: 3px solid #2d5016; padding: 15px;">🌱 CHECK YOUR ROOTS - Student Worksheet</h2>
              <div style="border: 1px solid #ddd; padding: 10px; margin: 15px 0;">
                <p><strong>Name:</strong> _________________ <strong>Date:</strong> _________ <strong>Class:</strong> _________</p>
              </div>
              ${lesson.check_your_roots?.grow_your_knowledge_questions ? 
                lesson.check_your_roots.grow_your_knowledge_questions.map((q, i) => 
                  `<p>${i + 1}. ${q}</p><div style="border-bottom: 1px solid #ccc; height: 30px; margin: 5px 0 10px 0;"></div>`
                ).join('') : ''}
            </div>
            
            <div class="worksheet-section" style="page-break-before: always; margin-top: 40px;">
              <h2 style="text-align: center; border: 3px solid #ff9800; padding: 15px;">🎨 CREATIVE ACTIVITY - Think It, Draw It, Create It!</h2>
              <div style="border: 1px solid #ddd; padding: 10px; margin: 15px 0;">
                <p><strong>Name:</strong> _________________ <strong>Date:</strong> _________ <strong>Class:</strong> _________</p>
              </div>
              <div style="border: 2px dashed #ccc; height: 250px; margin: 20px 0; display: flex; align-items: center; justify-content: center;">
                <p style="color: #999;">Drawing Space</p>
              </div>
            </div>
            
            <div class="worksheet-section" style="page-break-before: always; margin-top: 40px;">
              <h2 style="text-align: center; border: 3px solid #f44336; padding: 15px;">📝 TEST YOUR GROWTH - Assessment</h2>
              <div style="border: 1px solid #ddd; padding: 10px; margin: 15px 0;">
                <p><strong>Name:</strong> _________________ <strong>Date:</strong> _________ <strong>Class:</strong> _________</p>
              </div>
              ${lesson.test_your_growth?.multiple_choice ? 
                lesson.test_your_growth.multiple_choice.map((q, i) => 
                  `<p><strong>${i + 1}.</strong> ${q.question || q}</p>
                   <p style="margin-left: 15px;">A. _____ B. _____ C. _____ D. _____</p>`
                ).join('') : 
                `<p>1. _______________________________________________</p>
                 <p style="margin-left: 15px;">A. _____ B. _____ C. _____ D. _____</p>
                 <p>2. _______________________________________________</p>
                 <p style="margin-left: 15px;">A. _____ B. _____ C. _____ D. _____</p>`}
              ${lesson.test_your_growth?.short_answer ? 
                `<h4 style="margin-top: 20px;">Short Answer:</h4>` +
                lesson.test_your_growth.short_answer.map((q, i) => 
                  `<p>${i + 1}. ${q}</p><div style="border-bottom: 1px solid #ccc; height: 40px; margin: 5px 0 15px 0;"></div>`
                ).join('') : ''}
            </div>
          ` : ''}
        </div>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>All Lesson Plans - ROOTSPACE</title>
        <style>
          body { font-family: Georgia, serif; max-width: 800px; margin: 0 auto; padding: 40px; line-height: 1.6; }
          h1 { color: #2d5016; border-bottom: 3px solid #2d5016; padding-bottom: 10px; font-size: 24px; }
          h2 { color: #4a7c23; }
          h3 { color: #666; margin-top: 20px; }
          h4 { margin: 0 0 10px 0; }
          .header-info { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 20px 0; background: #f5f5f5; padding: 15px; border-radius: 8px; }
          .header-info div { border-bottom: 1px solid #ddd; padding: 5px 0; }
          .phase { margin: 15px 0; padding: 15px; border-radius: 8px; }
          .engage { background: #fef9c3; border-left: 4px solid #eab308; }
          .explore { background: #dbeafe; border-left: 4px solid #3b82f6; }
          .explain { background: #f3e8ff; border-left: 4px solid #9333ea; }
          .elaborate { background: #dcfce7; border-left: 4px solid #22c55e; }
          .evaluate { background: #fee2e2; border-left: 4px solid #ef4444; }
          .vocabulary { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0; }
          .vocab-word { background: #e8f5e9; padding: 4px 12px; border-radius: 20px; font-size: 14px; }
          .materials-list { columns: 2; margin: 10px 0; }
          .page-break { page-break-before: always; margin-top: 40px; padding-top: 20px; border-top: 3px solid #2d5016; }
          .lesson-plan { margin-bottom: 40px; }
          .cover-page { text-align: center; padding: 100px 20px; }
          .cover-page h1 { font-size: 36px; border: none; }
          .cover-page p { font-size: 18px; color: #666; }
          @media print { 
            body { padding: 20px; }
            .page-break { page-break-before: always; }
          }
        </style>
      </head>
      <body>
        <div class="cover-page">
          <h1>🌱 ROOTSPACE</h1>
          <h2>SAE 5E Lesson Plans Collection</h2>
          <p>Total Plans: ${lessonPlans.length}</p>
          <p>Generated: ${new Date().toLocaleDateString()}</p>
        </div>
        
        <div class="page-break"></div>
        
        ${lessonsHTML}
        
        <footer style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #888;">
          Generated by ROOTSPACE Growth Tool | rootspace.pro
        </footer>
        
        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Challenge Management Functions
  const loadChallenges = async (token = adminToken) => {
    try {
      const response = await axios.get(`${API}/challenges?include_scheduled=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenges(response.data.challenges || response.data || []);
    } catch (error) {
      console.error("Failed to load challenges:", error);
    }
  };

  const handleCreateChallenge = async () => {
    if (!newChallenge.title) {
      toast.error("Challenge title is required");
      return;
    }
    setLoading(true);
    try {
      // Filter out empty strings from arrays
      const challengeData = {
        ...newChallenge,
        requirements: newChallenge.requirements.filter(r => r.trim()),
        learning_objectives: newChallenge.learning_objectives.filter(o => o.trim()),
        featured_plants: newChallenge.featured_plants.filter(p => p.trim()),
        teacher_id: "admin",
        teacher_name: "ROOTSPACE Admin"
      };
      
      await axios.post(`${API}/admin/challenges`, challengeData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Challenge created!");
      setShowChallengeForm(false);
      resetChallengeForm();
      loadChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create challenge");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateChallenge = async () => {
    if (!editingChallenge) return;
    setLoading(true);
    try {
      const challengeData = {
        ...editingChallenge,
        requirements: editingChallenge.requirements?.filter(r => r.trim()) || [],
        learning_objectives: editingChallenge.learning_objectives?.filter(o => o.trim()) || [],
        featured_plants: editingChallenge.featured_plants?.filter(p => p.trim()) || []
      };
      
      await axios.put(`${API}/admin/challenges/${editingChallenge.id}`, challengeData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Challenge updated!");
      setEditingChallenge(null);
      loadChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update challenge");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    if (!window.confirm("Delete this challenge? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/challenges/${challengeId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Challenge deleted");
      loadChallenges();
      setSelectedChallenge(null);
    } catch (error) {
      toast.error("Failed to delete challenge");
    }
  };

  const resetChallengeForm = () => {
    setNewChallenge({
      title: "",
      description: "",
      theme: "edible",
      local_theme: "US Virgin Islands",
      prompt: "",
      requirements: [""],
      learning_objectives: [""],
      featured_plants: [""],
      grade_level: "K-12",
      min_plants: 5,
      max_plants: 30,
      yield_goal_lbs: null,
      water_limit_gallons: null,
      start_date: "",
      end_date: "",
      scheduled_publish_date: "",
      status: "scheduled",
      is_public: true,
      is_featured: true
    });
  };

  const addArrayItem = (field, isEditing = false) => {
    if (isEditing) {
      setEditingChallenge(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), ""]
      }));
    } else {
      setNewChallenge(prev => ({
        ...prev,
        [field]: [...prev[field], ""]
      }));
    }
  };

  const updateArrayItem = (field, index, value, isEditing = false) => {
    if (isEditing) {
      setEditingChallenge(prev => ({
        ...prev,
        [field]: prev[field].map((item, i) => i === index ? value : item)
      }));
    } else {
      setNewChallenge(prev => ({
        ...prev,
        [field]: prev[field].map((item, i) => i === index ? value : item)
      }));
    }
  };

  const removeArrayItem = (field, index, isEditing = false) => {
    if (isEditing) {
      setEditingChallenge(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }));
    } else {
      setNewChallenge(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }));
    }
  };

  // Inquiry Management Functions
  const loadInquiries = async (token = adminToken) => {
    try {
      const response = await axios.get(`${API}/admin/contact-submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(response.data || []);
    } catch (error) {
      console.error("Failed to load inquiries:", error);
    }
  };

  const loadTeachers = async (token = adminToken) => {
    try {
      const response = await axios.get(`${API}/admin/teachers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeachers(response.data || []);
    } catch (error) {
      console.error("Failed to load teachers:", error);
    }
  };

  const updateTeacherStatus = async (teacherId, status) => {
    try {
      await axios.put(`${API}/admin/teachers/${teacherId}`, 
        { verification_status: status },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(`Teacher ${status === 'verified' ? 'verified' : 'updated'} successfully`);
      loadTeachers();
    } catch (error) {
      toast.error("Failed to update teacher status");
    }
  };

  const markInquiryRead = async (inquiryId, read = true) => {
    try {
      await axios.put(`${API}/admin/contact-submissions/${inquiryId}`, 
        { read },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      loadInquiries();
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry(prev => ({ ...prev, read }));
      }
    } catch (error) {
      toast.error("Failed to update inquiry");
    }
  };

  const markInquiryResponded = async (inquiryId, responded = true) => {
    try {
      await axios.put(`${API}/admin/contact-submissions/${inquiryId}`, 
        { responded },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(responded ? "Marked as responded" : "Marked as not responded");
      loadInquiries();
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry(prev => ({ ...prev, responded }));
      }
    } catch (error) {
      toast.error("Failed to update inquiry");
    }
  };

  // Seed Orders functions
  const loadSeedOrders = async (token = adminToken) => {
    try {
      const params = orderFilter !== "all" ? `?status=${orderFilter}` : "";
      const response = await axios.get(`${API}/admin/seed-orders${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeedOrders(response.data.orders || []);
      setOrderStats(response.data.stats);
    } catch (error) {
      console.error("Failed to load seed orders:", error);
    }
  };

  const updateOrderStatus = async (orderId, status, trackingInfo = null) => {
    try {
      const updateData = { status };
      if (trackingInfo) {
        updateData.tracking_number = trackingInfo.tracking_number;
        updateData.shipping_carrier = trackingInfo.shipping_carrier;
      }
      
      await axios.patch(`${API}/admin/seed-orders/${orderId}`, updateData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`Order status updated to ${status}`);
      loadSeedOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status, ...updateData }));
      }
    } catch (error) {
      toast.error("Failed to update order");
    }
  };

  const getInterestLabel = (interest) => {
    const labels = {
      general: "General Question",
      advertising: "Advertising / Partnerships",
      press: "Press / Media",
      sponsorship: "Sponsorship Opportunities",
      wholesale: "Wholesale / Bulk Licensing",
      speaking: "Speaking / Workshop Requests",
      custom_design: "Custom Garden Design",
      government: "Government Partnership",
      ngo: "NGO Partnership",
      telco: "Telco Partnership",
      media: "Media or Press",
      sms_whatsapp: "SMS/WhatsApp Alerts",
      other: "Other"
    };
    return labels[interest] || interest;
  };

  const getInterestColor = (interest) => {
    const colors = {
      general: "bg-blue-900/50 text-blue-300",
      advertising: "bg-amber-900/50 text-amber-300",
      press: "bg-purple-900/50 text-purple-300",
      sponsorship: "bg-green-900/50 text-green-300",
      wholesale: "bg-cyan-900/50 text-cyan-300",
      speaking: "bg-pink-900/50 text-pink-300",
      custom_design: "bg-orange-900/50 text-orange-300",
      other: "bg-gray-900/50 text-gray-300"
    };
    return colors[interest] || "bg-gray-900/50 text-gray-300";
  };

  const filteredInquiries = inquiries.filter(inq => {
    if (inquiryFilter === "all") return true;
    if (inquiryFilter === "unread") return !inq.read;
    if (inquiryFilter === "responded") return inq.responded;
    return inq.interest === inquiryFilter;
  });

  const handleCreateCoupon = async () => {
    if (!newCoupon.code) {
      toast.error("Coupon code is required");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/coupons`, newCoupon, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Coupon created!");
      setNewCoupon({
        code: "", discount_type: "percent", discount_value: 10,
        valid_for_tiers: ["standard", "premium", "lifetime"],
        max_uses: null, expires_at: "", description: ""
      });
      loadCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create coupon");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCoupon = async (id) => {
    if (!confirm("Delete this coupon?")) return;
    try {
      await axios.delete(`${API}/admin/coupons/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Coupon deleted");
      loadCoupons();
    } catch (error) {
      toast.error("Failed to delete coupon");
    }
  };

  const handleToggleCoupon = async (id) => {
    try {
      await axios.put(`${API}/admin/coupons/${id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      loadCoupons();
    } catch (error) {
      toast.error("Failed to toggle coupon");
    }
  };

  const handleGrantPremium = async (e) => {
    e.preventDefault();
    if (!grantEmail) return;
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/grant-premium`,
        { email: grantEmail, tier: grantTier, reason: grantReason },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(`Premium access granted to ${grantEmail}`);
      setGrantEmail("");
      setGrantReason("");
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant premium");
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/export-excel`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rootspace_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Excel exported successfully");
    } catch (error) {
      toast.error("Failed to export Excel");
    } finally {
      setLoading(false);
    }
  };

  const handleSendNewsletter = async (sendNow = true) => {
    if (!newNewsletter.subject || !newNewsletter.content) {
      toast.error("Please fill in subject and content");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...newNewsletter,
        schedule_time: sendNow ? null : newNewsletter.schedule_time || null
      };

      await axios.post(
        `${API}/admin/newsletters`,
        payload,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );

      toast.success(sendNow ? "Newsletter sending started!" : "Newsletter scheduled!");
      setNewNewsletter({ subject: "", content: "", target_audience: "all", target_zone: "", target_filters: {}, schedule_time: "" });
      loadNewsletters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send newsletter");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSiteSettings = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/admin/site-settings`,
        siteSettings,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("Site settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateListing = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/marketplace/listings`,
        newListing,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("Listing created!");
      setNewListing({
        plant_name: "", variety: "", description: "", price: "",
        quantity_available: "", seed_count_per_packet: "", year_harvested: new Date().getFullYear(),
        origin_location: "", growing_instructions: "", planting_zone: "",
        days_to_germination: "", is_organic: false, is_heirloom: false
      });
      loadListings();
    } catch (error) {
      toast.error("Failed to create listing");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteListing = async (id) => {
    if (!confirm("Delete this listing?")) return;
    
    try {
      await axios.delete(`${API}/admin/marketplace/listings/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Listing deleted");
      loadListings();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  useEffect(() => {
    if (adminToken) {
      loadAllData();
    }
  }, [adminToken]);

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Login screen
  if (!adminToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-white flex items-center justify-center gap-2">
                <img src="/logo.png" alt="RootSpace" className="h-8 w-auto" />
                Admin
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-slate-300">Admin Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                      placeholder="Enter admin password"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="RootSpace" className="h-10 w-auto" />
            <span className="text-white font-bold text-lg">Admin</span>
          </div>
          <Button variant="outline" onClick={handleLogout} className="text-slate-300 border-slate-600">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex bg-slate-700/50 rounded-lg p-1 mb-6 overflow-x-auto">
          {[
            { id: "users", label: "Users", icon: Users },
            { id: "teachers", label: "Teachers", icon: GraduationCap, badge: teachers.filter(t => t.verification_status === 'pending').length },
            { id: "inquiries", label: "Inquiries", icon: MessageCircle, badge: inquiries.filter(i => !i.read).length },
            { id: "orders", label: "Seed Orders", icon: Package, badge: seedOrders.filter(o => o.status === 'confirmed').length },
            { id: "challenges", label: "Challenges", icon: Trophy },
            { id: "lessons", label: "Lesson Plans", icon: GraduationCap },
            { id: "pages", label: "Page Editor", icon: FileText },
            { id: "newsletters", label: "Newsletters", icon: Mail },
            { id: "coupons", label: "Coupons", icon: Tag },
            { id: "settings", label: "Site Settings", icon: Settings },
            { id: "marketplace", label: "Marketplace", icon: Package }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id ? "bg-primary text-white" : "text-slate-300 hover:text-white"
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Teachers Tab */}
        {activeTab === "teachers" && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <GraduationCap className="w-6 h-6 text-blue-400" /> Teacher Registrations
                </h2>
                <p className="text-slate-400 text-sm">Manage teacher accounts, grade levels, and disciplines</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  onClick={() => {
                    window.open(`${API}/admin/teachers/export?token=${adminToken}`, '_blank');
                  }}
                  data-testid="export-teachers-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <select
                  value={teacherFilter}
                  onChange={(e) => setTeacherFilter(e.target.value)}
                  className="bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="teacher-filter-select"
                >
                  <option value="all">All Teachers</option>
                  <option value="pending">Pending Verification</option>
                  <option value="verified">Verified</option>
                </select>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Total Teachers</p>
                    <p className="text-xl font-bold text-white">{teachers.length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <Clock className="w-8 h-8 text-amber-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Pending</p>
                    <p className="text-xl font-bold text-amber-400">
                      {teachers.filter(t => t.verification_status === 'pending').length}
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Verified</p>
                    <p className="text-xl font-bold text-green-400">
                      {teachers.filter(t => t.verification_status === 'verified').length}
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <Target className="w-8 h-8 text-purple-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Administrators</p>
                    <p className="text-xl font-bold text-purple-400">
                      {teachers.filter(t => t.grade_levels?.includes('Administrator')).length}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Teachers Grid */}
            <div className="grid lg:grid-cols-3 gap-4">
              {/* Teachers List */}
              <div className="lg:col-span-2 space-y-3">
                {teachers
                  .filter(t => {
                    if (teacherFilter === 'all') return true;
                    return t.verification_status === teacherFilter;
                  })
                  .map(teacher => (
                    <Card 
                      key={teacher.id}
                      className={`bg-slate-800/50 border-slate-700 cursor-pointer transition-all hover:border-blue-500 ${
                        selectedTeacher?.id === teacher.id ? 'border-blue-500 ring-1 ring-blue-500' : ''
                      }`}
                      onClick={() => setSelectedTeacher(teacher)}
                      data-testid={`teacher-card-${teacher.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                              <GraduationCap className="w-5 h-5 text-blue-400" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-white">{teacher.user_name}</h3>
                              <p className="text-slate-400 text-sm">{teacher.email}</p>
                              <p className="text-slate-500 text-xs">{teacher.school_name}</p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            teacher.verification_status === 'verified' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {teacher.verification_status}
                          </span>
                        </div>
                        
                        {/* Grade Levels */}
                        {teacher.grade_levels?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-slate-500 text-xs mb-1">Grade Levels:</p>
                            <div className="flex flex-wrap gap-1">
                              {teacher.grade_levels.map((level, i) => (
                                <span key={i} className={`px-2 py-0.5 rounded text-xs ${
                                  level === 'Administrator' 
                                    ? 'bg-purple-500/20 text-purple-400' 
                                    : 'bg-slate-700 text-slate-300'
                                }`}>
                                  {level}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Disciplines */}
                        {teacher.subjects?.length > 0 && (
                          <div className="mt-2">
                            <p className="text-slate-500 text-xs mb-1">Disciplines:</p>
                            <div className="flex flex-wrap gap-1">
                              {teacher.subjects.map((subj, i) => (
                                <span key={i} className={`px-2 py-0.5 rounded text-xs ${
                                  ['Special Education', 'Music', 'Cultural Education'].includes(subj)
                                    ? 'bg-green-500/20 text-green-400' 
                                    : 'bg-slate-700 text-slate-300'
                                }`}>
                                  {subj}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                
                {teachers.length === 0 && (
                  <div className="text-center py-12 text-slate-400">
                    <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No teacher registrations yet</p>
                  </div>
                )}
              </div>

              {/* Teacher Detail Panel */}
              <div className="lg:col-span-1">
                {selectedTeacher ? (
                  <Card className="bg-slate-800/50 border-slate-700 sticky top-4">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <GraduationCap className="w-5 h-5 text-blue-400" />
                        Teacher Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="text-slate-500 text-xs">Name</p>
                        <p className="text-white font-medium">{selectedTeacher.user_name}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Email</p>
                        <p className="text-white">{selectedTeacher.email}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">School</p>
                        <p className="text-white">{selectedTeacher.school_name || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">District</p>
                        <p className="text-white">{selectedTeacher.school_district || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">State/Region</p>
                        <p className="text-white">{selectedTeacher.state || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Registered</p>
                        <p className="text-white">
                          {new Date(selectedTeacher.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      <div className="border-t border-slate-700 pt-4">
                        <p className="text-slate-500 text-xs mb-2">Grade Levels</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedTeacher.grade_levels?.length > 0 ? (
                            selectedTeacher.grade_levels.map((level, i) => (
                              <span key={i} className={`px-2 py-1 rounded text-xs ${
                                level === 'Administrator' 
                                  ? 'bg-purple-500/20 text-purple-400 font-medium' 
                                  : 'bg-slate-700 text-slate-300'
                              }`}>
                                {level}
                              </span>
                            ))
                          ) : (
                            <span className="text-slate-500 text-sm">None selected</span>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-slate-500 text-xs mb-2">Disciplines</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedTeacher.subjects?.length > 0 ? (
                            selectedTeacher.subjects.map((subj, i) => (
                              <span key={i} className={`px-2 py-1 rounded text-xs ${
                                ['Special Education', 'Music', 'Cultural Education'].includes(subj)
                                  ? 'bg-green-500/20 text-green-400 font-medium' 
                                  : 'bg-slate-700 text-slate-300'
                              }`}>
                                {subj}
                              </span>
                            ))
                          ) : (
                            <span className="text-slate-500 text-sm">None selected</span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="border-t border-slate-700 pt-4 flex gap-2">
                        {selectedTeacher.verification_status !== 'verified' ? (
                          <Button 
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={() => updateTeacherStatus(selectedTeacher.id, 'verified')}
                            data-testid="verify-teacher-btn"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Verify Teacher
                          </Button>
                        ) : (
                          <Button 
                            variant="outline"
                            className="flex-1 border-amber-500 text-amber-500 hover:bg-amber-500/10"
                            onClick={() => updateTeacherStatus(selectedTeacher.id, 'pending')}
                          >
                            <Clock className="w-4 h-4 mr-2" />
                            Revoke Verification
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="py-12 text-center text-slate-400">
                      <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Select a teacher to view details</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Seed Orders Tab */}
        {activeTab === "orders" && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Package className="w-6 h-6 text-green-400" /> Seed Pack Orders
                </h2>
                <p className="text-slate-400 text-sm">Manage and fulfill seed pack orders</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={orderFilter}
                  onChange={(e) => { setOrderFilter(e.target.value); loadSeedOrders(); }}
                  className="bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="order-filter-select"
                >
                  <option value="all">All Orders</option>
                  <option value="awaiting_payment">Awaiting Payment</option>
                  <option value="confirmed">Confirmed (Ready)</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                </select>
                <Button variant="outline" onClick={() => loadSeedOrders()} className="text-slate-300 border-slate-600">
                  Refresh
                </Button>
              </div>
            </div>

            {/* Stats Row */}
            {orderStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="pt-4 flex items-center gap-3">
                    <Package className="w-8 h-8 text-blue-400" />
                    <div>
                      <p className="text-slate-400 text-xs">Total Orders</p>
                      <p className="text-xl font-bold text-white">{orderStats.total_orders}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="pt-4 flex items-center gap-3">
                    <Clock className="w-8 h-8 text-amber-400" />
                    <div>
                      <p className="text-slate-400 text-xs">Ready to Ship</p>
                      <p className="text-xl font-bold text-white">{orderStats.confirmed}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="pt-4 flex items-center gap-3">
                    <DollarSign className="w-8 h-8 text-green-400" />
                    <div>
                      <p className="text-slate-400 text-xs">Revenue</p>
                      <p className="text-xl font-bold text-white">${orderStats.total_revenue?.toFixed(2) || '0.00'}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="pt-4 flex items-center gap-3">
                    <Gift className="w-8 h-8 text-purple-400" />
                    <div>
                      <p className="text-slate-400 text-xs">VIDE Free</p>
                      <p className="text-xl font-bold text-white">{orderStats.vide_free_orders}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Orders Table */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Order ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Items</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {seedOrders.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center text-slate-400">
                            No orders found
                          </td>
                        </tr>
                      ) : (
                        seedOrders.map((order) => (
                          <tr key={order.id} className="hover:bg-slate-700/30">
                            <td className="px-4 py-3 text-sm text-white font-mono">
                              {order.id.slice(0, 8)}...
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-white">{order.user_name || 'Unknown'}</div>
                              <div className="text-xs text-slate-400">{order.user_email}</div>
                              {order.is_vide_teacher && (
                                <Badge className="mt-1 bg-purple-600 text-white text-xs">VIDE Teacher</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-300">
                              {order.items?.length || 0} items
                            </td>
                            <td className="px-4 py-3 text-sm text-white font-medium">
                              {order.is_vide_teacher ? (
                                <span className="text-purple-400">FREE</span>
                              ) : (
                                `$${order.total?.toFixed(2)}`
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={`text-xs ${
                                order.status === 'confirmed' ? 'bg-amber-600' :
                                order.status === 'shipped' ? 'bg-green-600' :
                                order.status === 'processing' ? 'bg-blue-600' :
                                order.status === 'awaiting_payment' ? 'bg-red-600' :
                                'bg-slate-600'
                              }`}>
                                {order.status}
                              </Badge>
                              {order.payment_status === 'paid' && order.status !== 'awaiting_payment' && (
                                <Badge className="ml-1 bg-green-700 text-xs">Paid</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-400">
                              {new Date(order.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-slate-300 hover:text-white"
                                  onClick={() => setSelectedOrder(order)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {order.status === 'confirmed' && (
                                  <Button 
                                    size="sm" 
                                    className="bg-blue-600 hover:bg-blue-700"
                                    onClick={() => updateOrderStatus(order.id, 'processing')}
                                  >
                                    Process
                                  </Button>
                                )}
                                {order.status === 'processing' && (
                                  <Button 
                                    size="sm" 
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => updateOrderStatus(order.id, 'shipped')}
                                  >
                                    Ship
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Order Detail Modal */}
            {selectedOrder && (
              <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
                <div className="bg-slate-800 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="p-6 border-b border-slate-700 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-white">Order Details</h3>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedOrder(null)} className="text-slate-400">
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-slate-400 text-sm">Order ID</p>
                        <p className="text-white font-mono">{selectedOrder.id}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-sm">Status</p>
                        <Badge className={`${
                          selectedOrder.status === 'shipped' ? 'bg-green-600' :
                          selectedOrder.status === 'confirmed' ? 'bg-amber-600' :
                          'bg-slate-600'
                        }`}>{selectedOrder.status}</Badge>
                      </div>
                      <div>
                        <p className="text-slate-400 text-sm">Customer</p>
                        <p className="text-white">{selectedOrder.user_name}</p>
                        <p className="text-slate-400 text-sm">{selectedOrder.user_email}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-sm">Total</p>
                        <p className="text-white text-lg font-bold">
                          {selectedOrder.is_vide_teacher ? 'FREE (VIDE)' : `$${selectedOrder.total?.toFixed(2)}`}
                        </p>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-slate-400 text-sm mb-2">Items</p>
                      <div className="space-y-2">
                        {selectedOrder.items?.map((item, i) => (
                          <div key={i} className="flex justify-between bg-slate-700/50 p-3 rounded">
                            <div>
                              <p className="text-white">{item.name}</p>
                              <p className="text-slate-400 text-sm">{item.type === 'pack' ? 'Seed Pack' : item.variety}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-white">×{item.quantity}</p>
                              <p className="text-slate-400 text-sm">${item.total_price?.toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {selectedOrder.shipping_address && Object.keys(selectedOrder.shipping_address).length > 0 && (
                      <div>
                        <p className="text-slate-400 text-sm mb-2">Shipping Address</p>
                        <div className="bg-slate-700/50 p-3 rounded text-white">
                          {selectedOrder.shipping_address.name && <p>{selectedOrder.shipping_address.name}</p>}
                          {selectedOrder.shipping_address.address && <p>{selectedOrder.shipping_address.address}</p>}
                          {selectedOrder.shipping_address.city && <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.zip}</p>}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-700">
                      {selectedOrder.status === 'confirmed' && (
                        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => updateOrderStatus(selectedOrder.id, 'processing')}>
                          Mark as Processing
                        </Button>
                      )}
                      {selectedOrder.status === 'processing' && (
                        <Button className="bg-green-600 hover:bg-green-700" onClick={() => updateOrderStatus(selectedOrder.id, 'shipped')}>
                          Mark as Shipped
                        </Button>
                      )}
                      <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => setSelectedOrder(null)}>
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Inquiries Tab */}
        {activeTab === "inquiries" && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <MessageCircle className="w-6 h-6 text-blue-400" /> Inquiries & Messages
                </h2>
                <p className="text-slate-400 text-sm">Contact form submissions and inquiries</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={inquiryFilter}
                  onChange={(e) => setInquiryFilter(e.target.value)}
                  className="bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="inquiry-filter-select"
                >
                  <option value="all">All Inquiries</option>
                  <option value="unread">Unread Only</option>
                  <option value="responded">Responded</option>
                  <option value="general">General Questions</option>
                  <option value="advertising">Advertising</option>
                  <option value="press">Press/Media</option>
                  <option value="sponsorship">Sponsorship</option>
                  <option value="wholesale">Wholesale</option>
                  <option value="speaking">Speaking</option>
                  <option value="custom_design">Custom Design</option>
                </select>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <MessageCircle className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Total Inquiries</p>
                    <p className="text-xl font-bold text-white">{inquiries.length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <Eye className="w-8 h-8 text-amber-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Unread</p>
                    <p className="text-xl font-bold text-white">{inquiries.filter(i => !i.read).length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <Reply className="w-8 h-8 text-green-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Responded</p>
                    <p className="text-xl font-bold text-white">{inquiries.filter(i => i.responded).length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <Clock className="w-8 h-8 text-purple-400" />
                  <div>
                    <p className="text-slate-400 text-xs">This Week</p>
                    <p className="text-xl font-bold text-white">
                      {inquiries.filter(i => {
                        const date = new Date(i.created_at);
                        const weekAgo = new Date();
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return date > weekAgo;
                      }).length}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Inquiries List & Detail */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Inquiries List */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Mail className="w-5 h-5 text-blue-400" /> All Messages ({filteredInquiries.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {filteredInquiries.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">No inquiries found</p>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {filteredInquiries.map((inquiry) => (
                        <div
                          key={inquiry.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedInquiry?.id === inquiry.id 
                              ? "bg-blue-900/30 border-blue-500" 
                              : inquiry.read 
                                ? "bg-slate-700/30 border-slate-600 hover:border-slate-500"
                                : "bg-slate-700/50 border-slate-500 hover:border-slate-400"
                          }`}
                          onClick={() => {
                            setSelectedInquiry(inquiry);
                            if (!inquiry.read) markInquiryRead(inquiry.id, true);
                          }}
                          data-testid={`inquiry-item-${inquiry.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {!inquiry.read && (
                                  <span className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0" />
                                )}
                                <h4 className={`font-medium truncate ${inquiry.read ? 'text-slate-300' : 'text-white'}`}>
                                  {inquiry.name}
                                </h4>
                                {inquiry.responded && (
                                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-xs text-slate-400 truncate">{inquiry.email}</p>
                              <div className="flex gap-2 mt-1 flex-wrap">
                                <span className={`text-xs px-2 py-0.5 rounded ${getInterestColor(inquiry.interest)}`}>
                                  {getInterestLabel(inquiry.interest)}
                                </span>
                              </div>
                            </div>
                            <div className="text-xs text-slate-500 text-right flex-shrink-0 ml-2">
                              {new Date(inquiry.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <p className="text-sm text-slate-400 mt-2 line-clamp-2">{inquiry.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Selected Inquiry Detail */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-blue-400" /> Inquiry Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedInquiry ? (
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-white">{selectedInquiry.name}</h3>
                          <a 
                            href={`mailto:${selectedInquiry.email}`} 
                            className="text-blue-400 hover:underline text-sm flex items-center gap-1"
                          >
                            {selectedInquiry.email}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${getInterestColor(selectedInquiry.interest)}`}>
                          {getInterestLabel(selectedInquiry.interest)}
                        </span>
                      </div>

                      {selectedInquiry.organization && (
                        <div>
                          <p className="text-xs text-slate-500">Organization</p>
                          <p className="text-white">{selectedInquiry.organization}</p>
                        </div>
                      )}

                      {selectedInquiry.country && (
                        <div>
                          <p className="text-xs text-slate-500">Country</p>
                          <p className="text-white">{selectedInquiry.country}</p>
                        </div>
                      )}

                      <div>
                        <p className="text-xs text-slate-500">Received</p>
                        <p className="text-white">
                          {new Date(selectedInquiry.created_at).toLocaleString()}
                        </p>
                      </div>

                      <div className="p-4 bg-slate-700/50 rounded-lg">
                        <p className="text-xs text-slate-500 mb-2">Message</p>
                        <p className="text-white whitespace-pre-wrap">{selectedInquiry.message}</p>
                      </div>

                      <div className="flex gap-2 pt-4 border-t border-slate-700">
                        <Button
                          onClick={() => window.open(`mailto:${selectedInquiry.email}?subject=Re: Your RootSpace Inquiry`, '_blank')}
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                          data-testid="reply-inquiry-btn"
                        >
                          <Reply className="w-4 h-4 mr-1" /> Reply via Email
                        </Button>
                        {!selectedInquiry.responded ? (
                          <Button
                            onClick={() => markInquiryResponded(selectedInquiry.id, true)}
                            variant="outline"
                            className="border-green-600 text-green-400 hover:bg-green-900/30"
                            data-testid="mark-responded-btn"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Mark Responded
                          </Button>
                        ) : (
                          <Button
                            onClick={() => markInquiryResponded(selectedInquiry.id, false)}
                            variant="outline"
                            className="border-slate-600 text-slate-400 hover:bg-slate-700"
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Unmark
                          </Button>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {selectedInquiry.read ? (
                          <Button
                            onClick={() => markInquiryRead(selectedInquiry.id, false)}
                            variant="ghost"
                            size="sm"
                            className="text-slate-400"
                          >
                            <EyeOff className="w-4 h-4 mr-1" /> Mark Unread
                          </Button>
                        ) : (
                          <Button
                            onClick={() => markInquiryRead(selectedInquiry.id, true)}
                            variant="ghost"
                            size="sm"
                            className="text-slate-400"
                          >
                            <Eye className="w-4 h-4 mr-1" /> Mark Read
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-400 text-center py-8">Select an inquiry to view details</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Challenges Tab */}
        {activeTab === "challenges" && (
          <div className="space-y-6">
            {/* Header with Create Button */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-amber-400" /> GROWTH Challenges
                </h2>
                <p className="text-slate-400 text-sm">Create, edit, and schedule monthly challenges</p>
              </div>
              <Button onClick={() => setShowChallengeForm(true)} className="bg-amber-500 hover:bg-amber-600">
                <Plus className="w-4 h-4 mr-2" /> Create Challenge
              </Button>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <Trophy className="w-8 h-8 text-amber-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Total Challenges</p>
                    <p className="text-xl font-bold text-white">{challenges.length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Active</p>
                    <p className="text-xl font-bold text-white">{challenges.filter(c => c.status === "active").length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <Clock className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Scheduled</p>
                    <p className="text-xl font-bold text-white">{challenges.filter(c => c.status === "scheduled").length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <Target className="w-8 h-8 text-purple-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Featured</p>
                    <p className="text-xl font-bold text-white">{challenges.filter(c => c.is_featured).length}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Challenges List */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-green-400" /> All Challenges
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {challenges.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">No challenges yet. Create your first one!</p>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {challenges.map((challenge) => (
                        <div
                          key={challenge.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedChallenge?.id === challenge.id 
                              ? "bg-amber-900/30 border-amber-500" 
                              : "bg-slate-700/50 border-slate-600 hover:border-slate-500"
                          }`}
                          onClick={() => setSelectedChallenge(challenge)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-white">{challenge.title}</h4>
                              <div className="flex gap-2 mt-1 flex-wrap">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  challenge.status === "active" ? "bg-green-900/50 text-green-300" :
                                  challenge.status === "scheduled" ? "bg-blue-900/50 text-blue-300" :
                                  "bg-gray-900/50 text-gray-300"
                                }`}>
                                  {challenge.status}
                                </span>
                                {challenge.is_featured && (
                                  <span className="text-xs bg-amber-900/50 text-amber-300 px-2 py-0.5 rounded">
                                    Featured
                                  </span>
                                )}
                                {challenge.theme && (
                                  <span className="text-xs bg-purple-900/50 text-purple-300 px-2 py-0.5 rounded">
                                    {challenge.theme}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-slate-500 text-right">
                              {challenge.scheduled_publish_date ? 
                                new Date(challenge.scheduled_publish_date).toLocaleDateString() :
                                challenge.submission_count ? `${challenge.submission_count} entries` : ""
                              }
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Selected Challenge Preview / Edit */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    {editingChallenge ? (
                      <><Edit className="w-5 h-5" /> Edit Challenge</>
                    ) : (
                      <><Trophy className="w-5 h-5 text-amber-400" /> Challenge Details</>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {editingChallenge ? (
                    <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                      <div>
                        <Label className="text-slate-300">Title</Label>
                        <Input
                          value={editingChallenge.title}
                          onChange={(e) => setEditingChallenge({...editingChallenge, title: e.target.value})}
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Description</Label>
                        <Textarea
                          value={editingChallenge.description}
                          onChange={(e) => setEditingChallenge({...editingChallenge, description: e.target.value})}
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-300">Status</Label>
                          <select
                            value={editingChallenge.status}
                            onChange={(e) => setEditingChallenge({...editingChallenge, status: e.target.value})}
                            className="w-full bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 mt-1"
                          >
                            <option value="draft">Draft</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="active">Active</option>
                            <option value="completed">Completed</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-slate-300">Theme</Label>
                          <select
                            value={editingChallenge.theme}
                            onChange={(e) => setEditingChallenge({...editingChallenge, theme: e.target.value})}
                            className="w-full bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 mt-1"
                          >
                            <option value="edible">Edible</option>
                            <option value="tropical">Tropical</option>
                            <option value="sustainable">Sustainable</option>
                            <option value="ecological">Ecological</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-300">Scheduled Publish Date</Label>
                        <Input
                          type="datetime-local"
                          value={editingChallenge.scheduled_publish_date?.slice(0, 16) || ""}
                          onChange={(e) => setEditingChallenge({...editingChallenge, scheduled_publish_date: e.target.value})}
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Requirements</Label>
                        {editingChallenge.requirements?.map((req, i) => (
                          <div key={i} className="flex gap-2 mt-1">
                            <Input
                              value={req}
                              onChange={(e) => updateArrayItem("requirements", i, e.target.value, true)}
                              className="bg-slate-700 border-slate-600 text-white"
                              placeholder="Requirement..."
                            />
                            <Button size="sm" variant="ghost" onClick={() => removeArrayItem("requirements", i, true)} className="text-red-400">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button size="sm" variant="outline" onClick={() => addArrayItem("requirements", true)} className="mt-2 text-slate-300">
                          <Plus className="w-3 h-3 mr-1" /> Add Requirement
                        </Button>
                      </div>
                      <div className="flex gap-2 pt-4 border-t border-slate-700">
                        <Button onClick={handleUpdateChallenge} className="flex-1 bg-green-600 hover:bg-green-700" disabled={loading}>
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
                        </Button>
                        <Button variant="outline" onClick={() => setEditingChallenge(null)} className="text-slate-300">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : selectedChallenge ? (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-bold text-white">{selectedChallenge.title}</h3>
                        <p className="text-slate-400 text-sm mt-1">{selectedChallenge.description}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Status</p>
                          <p className="text-white capitalize">{selectedChallenge.status}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Theme</p>
                          <p className="text-white capitalize">{selectedChallenge.theme}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Grade Level</p>
                          <p className="text-white">{selectedChallenge.grade_level}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Submissions</p>
                          <p className="text-white">{selectedChallenge.submission_count || 0}</p>
                        </div>
                      </div>
                      {selectedChallenge.featured_plants?.length > 0 && (
                        <div>
                          <p className="text-slate-500 text-sm mb-1">Featured Plants</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedChallenge.featured_plants.map((plant, i) => (
                              <span key={i} className="text-xs bg-green-900/50 text-green-300 px-2 py-0.5 rounded">
                                {plant}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedChallenge.requirements?.length > 0 && (
                        <div>
                          <p className="text-slate-500 text-sm mb-1">Requirements</p>
                          <ul className="text-sm text-slate-300 list-disc list-inside">
                            {selectedChallenge.requirements.map((req, i) => (
                              <li key={i}>{req}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="flex gap-2 pt-4 border-t border-slate-700">
                        <Button onClick={() => setEditingChallenge({...selectedChallenge})} className="flex-1 bg-blue-600 hover:bg-blue-700">
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={() => handleDeleteChallenge(selectedChallenge.id)}
                          className="border-red-600 text-red-400 hover:bg-red-900/30"
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-400 text-center py-8">Select a challenge to view details</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Create Challenge Modal */}
            {showChallengeForm && (
              <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                <Card className="bg-slate-800 border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Plus className="w-5 h-5" /> Create New Challenge
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-slate-300">Title *</Label>
                      <Input
                        value={newChallenge.title}
                        onChange={(e) => setNewChallenge({...newChallenge, title: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white mt-1"
                        placeholder="🌿 Monthly Challenge Title"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Description</Label>
                      <Textarea
                        value={newChallenge.description}
                        onChange={(e) => setNewChallenge({...newChallenge, description: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white mt-1"
                        rows={3}
                        placeholder="Describe the challenge..."
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Prompt</Label>
                      <Textarea
                        value={newChallenge.prompt}
                        onChange={(e) => setNewChallenge({...newChallenge, prompt: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white mt-1"
                        rows={2}
                        placeholder="What should participants create?"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Theme</Label>
                        <select
                          value={newChallenge.theme}
                          onChange={(e) => setNewChallenge({...newChallenge, theme: e.target.value})}
                          className="w-full bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 mt-1"
                        >
                          <option value="edible">Edible</option>
                          <option value="tropical">Tropical</option>
                          <option value="sustainable">Sustainable</option>
                          <option value="ecological">Ecological</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Grade Level</Label>
                        <Input
                          value={newChallenge.grade_level}
                          onChange={(e) => setNewChallenge({...newChallenge, grade_level: e.target.value})}
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                          placeholder="K-12"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300">Scheduled Publish Date (First Thursday of month)</Label>
                      <Input
                        type="datetime-local"
                        value={newChallenge.scheduled_publish_date}
                        onChange={(e) => setNewChallenge({...newChallenge, scheduled_publish_date: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white mt-1"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300">Requirements</Label>
                        <Button size="sm" variant="ghost" onClick={() => addArrayItem("requirements")} className="text-slate-400">
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      </div>
                      {newChallenge.requirements.map((req, i) => (
                        <div key={i} className="flex gap-2 mt-1">
                          <Input
                            value={req}
                            onChange={(e) => updateArrayItem("requirements", i, e.target.value)}
                            className="bg-slate-700 border-slate-600 text-white"
                            placeholder="e.g., Include at least 5 plants"
                          />
                          <Button size="sm" variant="ghost" onClick={() => removeArrayItem("requirements", i)} className="text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300">Featured Plants</Label>
                        <Button size="sm" variant="ghost" onClick={() => addArrayItem("featured_plants")} className="text-slate-400">
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      </div>
                      {newChallenge.featured_plants.map((plant, i) => (
                        <div key={i} className="flex gap-2 mt-1">
                          <Input
                            value={plant}
                            onChange={(e) => updateArrayItem("featured_plants", i, e.target.value)}
                            className="bg-slate-700 border-slate-600 text-white"
                            placeholder="e.g., Mango, Papaya"
                          />
                          <Button size="sm" variant="ghost" onClick={() => removeArrayItem("featured_plants", i)} className="text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Yield Goal (lbs)</Label>
                        <Input
                          type="number"
                          value={newChallenge.yield_goal_lbs || ""}
                          onChange={(e) => setNewChallenge({...newChallenge, yield_goal_lbs: e.target.value ? Number(e.target.value) : null})}
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                          placeholder="Optional"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Water Limit (gallons)</Label>
                        <Input
                          type="number"
                          value={newChallenge.water_limit_gallons || ""}
                          onChange={(e) => setNewChallenge({...newChallenge, water_limit_gallons: e.target.value ? Number(e.target.value) : null})}
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-4 border-t border-slate-700">
                      <Button onClick={handleCreateChallenge} className="flex-1 bg-amber-500 hover:bg-amber-600" disabled={loading}>
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create Challenge</>}
                      </Button>
                      <Button variant="outline" onClick={() => {setShowChallengeForm(false); resetChallengeForm();}} className="text-slate-300">
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Page Editor Tab */}
        {activeTab === "pages" && (
          <CMSPageEditor adminToken={adminToken} />
        )}

        {/* Lesson Plans Tab */}
        {activeTab === "lessons" && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Stats & Email Settings */}
            <div className="lg:col-span-3 grid md:grid-cols-4 gap-4 mb-2">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <GraduationCap className="w-8 h-8 text-green-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Total Lesson Plans</p>
                    <p className="text-xl font-bold text-white">{lessonPlans.length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700 md:col-span-3">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <span className="text-slate-300 text-sm">Email notifications to:</span>
                    <Input
                      type="email"
                      placeholder="admin@example.com"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      className="flex-1 bg-slate-700 border-slate-600 text-white max-w-xs"
                    />
                    <Button size="sm" onClick={updateAdminEmail} className="bg-green-600 hover:bg-green-700">
                      <Save className="w-4 h-4 mr-1" /> Save
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Lesson Plans List */}
            <div className="lg:col-span-2 space-y-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5" /> All Lesson Plans
                  </CardTitle>
                  {lessonPlans.length > 0 && (
                    <Button 
                      size="sm" 
                      onClick={downloadAllLessonsPDF}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Download className="w-4 h-4 mr-1" /> Export All ({lessonPlans.length})
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {lessonPlans.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">No lesson plans generated yet. Use the Growth Tool to create some!</p>
                  ) : (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {lessonPlans.map((lesson) => (
                        <div
                          key={lesson.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedLesson?.id === lesson.id 
                              ? "bg-green-900/30 border-green-500" 
                              : "bg-slate-700/50 border-slate-600 hover:border-slate-500"
                          }`}
                          onClick={() => setSelectedLesson(lesson)}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium text-white">{lesson.title || "Untitled"}</h4>
                              <p className="text-xs text-slate-400 mt-1">{lesson.standard || "No standard"}</p>
                              <div className="flex gap-2 mt-2">
                                {lesson.grade_level && (
                                  <span className="text-xs bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded">
                                    {lesson.grade_level}
                                  </span>
                                )}
                                {lesson.content_area && (
                                  <span className="text-xs bg-green-900/50 text-green-300 px-2 py-0.5 rounded">
                                    {lesson.content_area}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-slate-500">
                              {new Date(lesson.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Selected Lesson Preview */}
            <div className="lg:col-span-1">
              <Card className="bg-slate-800/50 border-slate-700 sticky top-4">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <GraduationCap className="w-5 h-5" /> Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedLesson ? (
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-bold text-lg text-white">{selectedLesson.title}</h3>
                        <p className="text-slate-400 text-sm mt-1">{selectedLesson.standard}</p>
                      </div>
                      
                      {selectedLesson.lesson_plan?.key_concepts && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase">Key Concepts</p>
                          <p className="text-slate-300 text-sm">{selectedLesson.lesson_plan.key_concepts}</p>
                        </div>
                      )}
                      
                      {selectedLesson.lesson_plan?.vocabulary && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase mb-1">Vocabulary</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedLesson.lesson_plan.vocabulary.slice(0, 5).map((v, i) => (
                              <span key={i} className="text-xs bg-amber-900/50 text-amber-300 px-2 py-0.5 rounded">
                                {v}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex gap-2 pt-4 border-t border-slate-700">
                        <Button 
                          size="sm" 
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => openLessonPDF(selectedLesson.id)}
                        >
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1 border-red-600 text-red-400 hover:bg-red-900/30"
                          onClick={() => deleteLesson(selectedLesson.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-400 text-center py-8">Select a lesson plan to preview</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Stats */}
            <div className="lg:col-span-3 grid md:grid-cols-3 gap-4 mb-2">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Total Users</p>
                    <p className="text-xl font-bold text-white">{users.length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <Crown className="w-8 h-8 text-amber-400" />
                  <div>
                    <p className="text-slate-400 text-xs">Premium</p>
                    <p className="text-xl font-bold text-white">{users.filter(u => u.subscription_tier === "premium").length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4 flex items-center gap-3">
                  <GraduationCap className="w-8 h-8 text-green-400" />
                  <div>
                    <p className="text-slate-400 text-xs">EDU Verified</p>
                    <p className="text-xl font-bold text-white">{users.filter(u => u.edu_verified).length}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Grant Premium */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-primary" />
                  Grant Premium
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleGrantPremium} className="space-y-3">
                  <Input
                    type="email"
                    value={grantEmail}
                    onChange={(e) => setGrantEmail(e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="user@example.com"
                  />
                  <select
                    value={grantTier}
                    onChange={(e) => setGrantTier(e.target.value)}
                    className="w-full rounded-md bg-slate-900/50 border border-slate-600 text-white p-2"
                  >
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                  <Input
                    value={grantReason}
                    onChange={(e) => setGrantReason(e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="Reason (optional)"
                  />
                  <Button type="submit" className="w-full" disabled={loading || !grantEmail}>
                    Grant Premium
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Users List */}
            <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white text-lg">Users</CardTitle>
                <Button variant="outline" onClick={handleExportExcel} className="text-slate-300 border-slate-600" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </CardHeader>
              <CardContent>
                <div className="mb-3 relative">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                    placeholder="Search..."
                  />
                </div>
                <div className="max-h-[350px] overflow-y-auto space-y-2">
                  {filteredUsers.map((user, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-900/30 rounded">
                      <div>
                        <p className="text-white text-sm">{user.name || user.email}</p>
                        <p className="text-slate-500 text-xs">{user.email}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        user.subscription_tier === "premium" ? "bg-amber-500/20 text-amber-400" :
                        user.subscription_tier === "standard" ? "bg-green-500/20 text-green-400" :
                        "bg-slate-500/20 text-slate-400"
                      }`}>
                        {user.subscription_tier || "free"}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Newsletters Tab */}
        {activeTab === "newsletters" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" />
                  Create Newsletter
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  value={newNewsletter.subject}
                  onChange={(e) => setNewNewsletter(n => ({...n, subject: e.target.value}))}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  placeholder="Subject"
                  data-testid="newsletter-subject"
                />
                
                {/* Target Audience Dropdown */}
                <div>
                  <Label className="text-slate-300 text-sm mb-1 block">Target Audience</Label>
                  <select
                    value={newNewsletter.target_audience}
                    onChange={(e) => setNewNewsletter(n => ({...n, target_audience: e.target.value, target_filters: {}}))}
                    className="w-full rounded-md bg-slate-900/50 border border-slate-600 text-white p-2"
                    data-testid="newsletter-audience"
                  >
                    <option value="all">All Users</option>
                    <option value="free">Free Tier Only</option>
                    <option value="standard">Standard Tier Only</option>
                    <option value="premium">Premium Tier Only</option>
                    <option value="lifetime">Lifetime Members</option>
                    <option value="zone_specific">By Planting Zone</option>
                    <option value="sellers">Seed Sellers (has listings)</option>
                    <option value="buyers">Seed Buyers (has purchases)</option>
                  </select>
                </div>

                {/* Zone Input (shown when zone_specific selected) */}
                {newNewsletter.target_audience === "zone_specific" && (
                  <div>
                    <Label className="text-slate-300 text-sm mb-1 block">Planting Zone</Label>
                    <Input
                      value={newNewsletter.target_zone}
                      onChange={(e) => setNewNewsletter(n => ({...n, target_zone: e.target.value}))}
                      className="bg-slate-900/50 border-slate-600 text-white"
                      placeholder="e.g., 7a, 8b, 9a"
                      data-testid="newsletter-zone"
                    />
                  </div>
                )}

                {/* Advanced Filters for sellers/buyers */}
                {(newNewsletter.target_audience === "sellers" || newNewsletter.target_audience === "buyers") && (
                  <div className="p-3 bg-slate-900/30 rounded-lg space-y-2">
                    <p className="text-slate-400 text-xs flex items-center gap-1">
                      <Filter className="w-3 h-3" /> Advanced Filters (optional)
                    </p>
                    <Input
                      value={newNewsletter.target_filters?.plant_filter || ""}
                      onChange={(e) => setNewNewsletter(n => ({
                        ...n, 
                        target_filters: {...n.target_filters, plant_filter: e.target.value}
                      }))}
                      className="bg-slate-900/50 border-slate-600 text-white text-sm"
                      placeholder={newNewsletter.target_audience === "sellers" ? "Filter: sells plant (e.g., Tomato)" : "Filter: bought plant (e.g., Pepper)"}
                    />
                  </div>
                )}

                <Textarea
                  value={newNewsletter.content}
                  onChange={(e) => setNewNewsletter(n => ({...n, content: e.target.value}))}
                  className="bg-slate-900/50 border-slate-600 text-white min-h-[150px] font-mono text-sm"
                  placeholder="HTML content. Use {{name}}, {{tier}}, {{zone}}, {{seeds_count}}, {{listings_count}}"
                  data-testid="newsletter-content"
                />
                <Input
                  type="datetime-local"
                  value={newNewsletter.schedule_time}
                  onChange={(e) => setNewNewsletter(n => ({...n, schedule_time: e.target.value}))}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  data-testid="newsletter-schedule"
                />
                <div className="flex gap-2">
                  <Button onClick={() => handleSendNewsletter(true)} className="flex-1" disabled={loading} data-testid="newsletter-send-now">
                    <Send className="w-4 h-4 mr-2" />
                    Send Now
                  </Button>
                  {newNewsletter.schedule_time && (
                    <Button onClick={() => handleSendNewsletter(false)} variant="outline" className="flex-1 border-slate-600" data-testid="newsletter-schedule-btn">
                      <Calendar className="w-4 h-4 mr-2" />
                      Schedule
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {newsletters.map((nl) => (
                    <div key={nl.id} className="p-2 bg-slate-900/30 rounded">
                      <div className="flex justify-between items-start">
                        <p className="text-white text-sm font-medium">{nl.subject}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          nl.status === "sent" ? "bg-green-500/20 text-green-400" :
                          nl.status === "scheduled" ? "bg-amber-500/20 text-amber-400" :
                          "bg-slate-500/20 text-slate-400"
                        }`}>
                          {nl.status}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">
                        Target: {nl.target_audience} {nl.target_zone && `(Zone ${nl.target_zone})`}
                      </p>
                      {nl.status === "sent" && (
                        <p className="text-xs text-slate-500">✓ {nl.sent_count} sent</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Coupons Tab */}
        {activeTab === "coupons" && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Create Coupon */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Tag className="w-5 h-5 text-primary" />
                  Create Coupon
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-slate-300 text-sm">Coupon Code</Label>
                  <Input
                    value={newCoupon.code}
                    onChange={(e) => setNewCoupon(c => ({...c, code: e.target.value.toUpperCase()}))}
                    className="bg-slate-900/50 border-slate-600 text-white font-mono"
                    placeholder="e.g., SPRING25"
                    data-testid="coupon-code"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-slate-300 text-sm">Discount Type</Label>
                    <select
                      value={newCoupon.discount_type}
                      onChange={(e) => setNewCoupon(c => ({...c, discount_type: e.target.value}))}
                      className="w-full rounded-md bg-slate-900/50 border border-slate-600 text-white p-2"
                      data-testid="coupon-type"
                    >
                      <option value="percent">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Value</Label>
                    <Input
                      type="number"
                      value={newCoupon.discount_value}
                      onChange={(e) => setNewCoupon(c => ({...c, discount_value: parseFloat(e.target.value) || 0}))}
                      className="bg-slate-900/50 border-slate-600 text-white"
                      placeholder="10"
                      data-testid="coupon-value"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Valid For Tiers</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {["standard", "premium", "lifetime"].map(tier => (
                      <label key={tier} className="flex items-center gap-1.5 text-slate-300 text-sm">
                        <Checkbox
                          checked={newCoupon.valid_for_tiers.includes(tier)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setNewCoupon(c => ({...c, valid_for_tiers: [...c.valid_for_tiers, tier]}));
                            } else {
                              setNewCoupon(c => ({...c, valid_for_tiers: c.valid_for_tiers.filter(t => t !== tier)}));
                            }
                          }}
                        />
                        {tier.charAt(0).toUpperCase() + tier.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-slate-300 text-sm">Max Uses</Label>
                    <Input
                      type="number"
                      value={newCoupon.max_uses || ""}
                      onChange={(e) => setNewCoupon(c => ({...c, max_uses: e.target.value ? parseInt(e.target.value) : null}))}
                      className="bg-slate-900/50 border-slate-600 text-white"
                      placeholder="Unlimited"
                      data-testid="coupon-max-uses"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Expires</Label>
                    <Input
                      type="datetime-local"
                      value={newCoupon.expires_at}
                      onChange={(e) => setNewCoupon(c => ({...c, expires_at: e.target.value}))}
                      className="bg-slate-900/50 border-slate-600 text-white"
                      data-testid="coupon-expires"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Description (optional)</Label>
                  <Input
                    value={newCoupon.description}
                    onChange={(e) => setNewCoupon(c => ({...c, description: e.target.value}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="Spring sale discount"
                  />
                </div>
                <Button onClick={handleCreateCoupon} className="w-full" disabled={loading || !newCoupon.code} data-testid="create-coupon-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Coupon
                </Button>
              </CardContent>
            </Card>

            {/* Coupons List */}
            <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-white text-lg">Active Coupons ({coupons.filter(c => c.active).length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-[500px] overflow-y-auto space-y-2">
                  {coupons.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-4">No coupons created yet</p>
                  ) : (
                    coupons.map((coupon) => (
                      <div key={coupon.id} className={`flex items-center justify-between p-3 rounded ${coupon.active ? 'bg-slate-900/30' : 'bg-slate-900/10 opacity-60'}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-white font-bold">{coupon.code}</span>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              coupon.discount_type === "percent" 
                                ? "bg-green-500/20 text-green-400" 
                                : "bg-blue-500/20 text-blue-400"
                            }`}>
                              {coupon.discount_type === "percent" ? `${coupon.discount_value}% OFF` : `$${coupon.discount_value} OFF`}
                            </span>
                            {!coupon.active && <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">Disabled</span>}
                          </div>
                          <p className="text-slate-500 text-xs mt-1">
                            {coupon.description || "No description"} • {coupon.times_used || 0}{coupon.max_uses ? `/${coupon.max_uses}` : ""} uses
                            {coupon.expires_at && ` • Expires ${new Date(coupon.expires_at).toLocaleDateString()}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleCoupon(coupon.id)}
                            className="text-slate-400 hover:text-white"
                            title={coupon.active ? "Disable" : "Enable"}
                          >
                            {coupon.active ? <ToggleRight className="w-5 h-5 text-green-400" /> : <ToggleLeft className="w-5 h-5" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCoupon(coupon.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Site Settings Tab */}
        {activeTab === "settings" && siteSettings && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary" />
                  Homepage Content
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300">Hero Title</Label>
                  <Input
                    value={siteSettings.hero_title || ""}
                    onChange={(e) => setSiteSettings(s => ({...s, hero_title: e.target.value}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Hero Subtitle</Label>
                  <Textarea
                    value={siteSettings.hero_subtitle || ""}
                    onChange={(e) => setSiteSettings(s => ({...s, hero_subtitle: e.target.value}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    rows={3}
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Announcement Banner</Label>
                  <Input
                    value={siteSettings.announcement_banner || ""}
                    onChange={(e) => setSiteSettings(s => ({...s, announcement_banner: e.target.value}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="Special announcement message"
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <Checkbox
                      checked={siteSettings.announcement_active}
                      onCheckedChange={(v) => setSiteSettings(s => ({...s, announcement_active: v}))}
                    />
                    <span className="text-slate-300 text-sm">Show announcement</span>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Footer Text</Label>
                  <Input
                    value={siteSettings.footer_text || ""}
                    onChange={(e) => setSiteSettings(s => ({...s, footer_text: e.target.value}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Contact Email</Label>
                  <Input
                    value={siteSettings.contact_email || ""}
                    onChange={(e) => setSiteSettings(s => ({...s, contact_email: e.target.value}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <Button onClick={handleSaveSiteSettings} className="w-full" disabled={loading}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Social Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300">Instagram</Label>
                  <Input
                    value={siteSettings.social_links?.instagram || ""}
                    onChange={(e) => setSiteSettings(s => ({...s, social_links: {...s.social_links, instagram: e.target.value}}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="https://instagram.com/..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Facebook</Label>
                  <Input
                    value={siteSettings.social_links?.facebook || ""}
                    onChange={(e) => setSiteSettings(s => ({...s, social_links: {...s.social_links, facebook: e.target.value}}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="https://facebook.com/..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Twitter/X</Label>
                  <Input
                    value={siteSettings.social_links?.twitter || ""}
                    onChange={(e) => setSiteSettings(s => ({...s, social_links: {...s.social_links, twitter: e.target.value}}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="https://twitter.com/..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">YouTube</Label>
                  <Input
                    value={siteSettings.social_links?.youtube || ""}
                    onChange={(e) => setSiteSettings(s => ({...s, social_links: {...s.social_links, youtube: e.target.value}}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="https://youtube.com/..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">TikTok</Label>
                  <Input
                    value={siteSettings.social_links?.tiktok || ""}
                    onChange={(e) => setSiteSettings(s => ({...s, social_links: {...s.social_links, tiktok: e.target.value}}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="https://tiktok.com/@..."
                  />
                </div>
                <Button onClick={handleSaveSiteSettings} className="w-full mt-2" disabled={loading}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Social Links
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Marketplace Tab */}
        {activeTab === "marketplace" && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Add Listing */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Plus className="w-5 h-5 text-primary" />
                  Add Listing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
                <Input
                  value={newListing.plant_name}
                  onChange={(e) => setNewListing(l => ({...l, plant_name: e.target.value}))}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  placeholder="Plant Name"
                />
                <Input
                  value={newListing.variety}
                  onChange={(e) => setNewListing(l => ({...l, variety: e.target.value}))}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  placeholder="Variety"
                />
                <Textarea
                  value={newListing.description}
                  onChange={(e) => setNewListing(l => ({...l, description: e.target.value}))}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  placeholder="Description"
                  rows={3}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    value={newListing.price}
                    onChange={(e) => setNewListing(l => ({...l, price: e.target.value}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="Price"
                  />
                  <Input
                    type="number"
                    value={newListing.quantity_available}
                    onChange={(e) => setNewListing(l => ({...l, quantity_available: e.target.value}))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="Qty"
                  />
                </div>
                <Input
                  type="number"
                  value={newListing.seed_count_per_packet}
                  onChange={(e) => setNewListing(l => ({...l, seed_count_per_packet: e.target.value}))}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  placeholder="Seeds per packet"
                />
                <Input
                  value={newListing.origin_location}
                  onChange={(e) => setNewListing(l => ({...l, origin_location: e.target.value}))}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  placeholder="Origin (e.g., Austin, TX)"
                />
                <Textarea
                  value={newListing.growing_instructions}
                  onChange={(e) => setNewListing(l => ({...l, growing_instructions: e.target.value}))}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  placeholder="Growing instructions"
                  rows={3}
                />
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-slate-300 text-sm">
                    <Checkbox
                      checked={newListing.is_organic}
                      onCheckedChange={(v) => setNewListing(l => ({...l, is_organic: v}))}
                    />
                    Organic
                  </label>
                  <label className="flex items-center gap-2 text-slate-300 text-sm">
                    <Checkbox
                      checked={newListing.is_heirloom}
                      onCheckedChange={(v) => setNewListing(l => ({...l, is_heirloom: v}))}
                    />
                    Heirloom
                  </label>
                </div>
                <Button onClick={handleCreateListing} className="w-full" disabled={loading}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Listing
                </Button>
              </CardContent>
            </Card>

            {/* Listings */}
            <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-white text-lg">All Listings ({listings.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-[550px] overflow-y-auto space-y-2">
                  {listings.map((listing) => (
                    <div key={listing.id} className="flex items-center justify-between p-3 bg-slate-900/30 rounded">
                      <div className="flex-1">
                        <p className="text-white font-medium">{listing.plant_name} {listing.variety && `- ${listing.variety}`}</p>
                        <p className="text-slate-500 text-xs">
                          ${listing.price} • {listing.quantity_available} available • {listing.seller_name}
                        </p>
                        <div className="flex gap-2 mt-1">
                          {listing.is_organic && <span className="text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">Organic</span>}
                          {listing.is_heirloom && <span className="text-xs bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">Heirloom</span>}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteListing(listing.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
